import { jwtDecode } from "jwt-decode";

/** nginx rejects Authorization above ~8–10KB; oversized JWTs cause 400 + fake CORS errors. */
const MAX_SESSION_TOKEN_CHARS = 7500;

type TokenClaims = {
  userId?: string;
  entityId?: string;
  currentEntityId?: string;
  role?: string;
  permissions?: Record<string, any>;
  memberships?: any[];
  email?: string;
  isAdvisor?: boolean;
  isPartner?: boolean;
  isGodModeUser?: boolean;
  /** Account-wide privilege (User document); also see permissions.canConfigureAriaChatPreviewSettings */
  canConfigureAriaChatPreviewSettings?: boolean;
  isTempByGodModeUser?: boolean;
  isPrivilegedImpersonation?: boolean;
  teamMemberAccess?: boolean;
  teamIds?: string[];
};

const hasOwn = (obj: Record<string, any>, key: string) =>
  Object.prototype.hasOwnProperty.call(obj, key);

export const sanitizeAuthPayload = (payload: any) => {
  if (!payload || typeof payload !== "object" || !payload.token || !payload.user) {
    return payload;
  }

  let claims: TokenClaims;
  try {
    claims = jwtDecode<TokenClaims>(payload.token);
  } catch {
    return payload;
  }

  const user = { ...(payload.user || {}) };
  const claimRecord = claims as Record<string, any>;

  // Security-sensitive authorization fields always come from JWT claims.
  if (hasOwn(claimRecord, "userId")) user.userId = claims.userId;
  if (hasOwn(claimRecord, "entityId")) user.entityId = claims.entityId;
  if (hasOwn(claimRecord, "currentEntityId")) user.currentEntityId = claims.currentEntityId;
  if (hasOwn(claimRecord, "role")) user.role = claims.role;
  if (hasOwn(claimRecord, "permissions")) user.permissions = claims.permissions;
  if (hasOwn(claimRecord, "memberships")) user.memberships = claims.memberships;
  if (hasOwn(claimRecord, "email")) user.email = claims.email;
  if (hasOwn(claimRecord, "isAdvisor")) user.isAdvisor = !!claims.isAdvisor;
  if (hasOwn(claimRecord, "isPartner")) user.isPartner = !!claims.isPartner;
  if (hasOwn(claimRecord, "isGodModeUser")) user.isGodModeUser = !!claims.isGodModeUser;
  if (hasOwn(claimRecord, "canConfigureAriaChatPreviewSettings")) {
    user.canConfigureAriaChatPreviewSettings = !!claims.canConfigureAriaChatPreviewSettings;
  }
  if (hasOwn(claimRecord, "isTempByGodModeUser")) user.isTempByGodModeUser = !!claims.isTempByGodModeUser;
  if (hasOwn(claimRecord, "isPrivilegedImpersonation")) {
    user.isPrivilegedImpersonation = !!claims.isPrivilegedImpersonation;
  }
  if (hasOwn(claimRecord, "teamMemberAccess")) user.teamMemberAccess = !!claims.teamMemberAccess;
  if (hasOwn(claimRecord, "teamIds")) user.teamIds = Array.isArray(claims.teamIds) ? claims.teamIds : [];

  return {
    ...payload,
    user,
  };
};

const CRM_ACTING_ADVISOR_KEY = "crm.actingAdvisorId";
const CRM_ACTING_ADVISOR_OWNER_KEY = "crm.actingAdvisorId.ownerUserId";

/** Keep CRM scope aligned with the signed-in user after impersonation / account switch. */
function syncCrmActingAdvisorSession(user: Record<string, unknown> | undefined) {
  if (!user) return;
  try {
    const selfId = String(user.userId ?? user._id ?? "").trim();
    if (!selfId) return;
    const prevOwner = localStorage.getItem(CRM_ACTING_ADVISOR_OWNER_KEY)?.trim();
    const impersonating = user.isPrivilegedImpersonation === true;
    if (impersonating || (prevOwner && prevOwner !== selfId)) {
      localStorage.setItem(CRM_ACTING_ADVISOR_KEY, selfId);
      localStorage.setItem(CRM_ACTING_ADVISOR_OWNER_KEY, selfId);
    }
  } catch {
    /* ignore */
  }
}

export const storeUserWithTokenClaims = (payload: any) => {
  const sanitizedPayload = sanitizeAuthPayload(payload);
  const token = sanitizedPayload?.token;
  if (typeof token === "string" && token.length > MAX_SESSION_TOKEN_CHARS) {
    console.error(
      `[auth] Session token is too large (${token.length} chars). Clear site data and log in again after the API is updated.`,
    );
    clearOversizedSession();
    throw new Error("Session token is too large. Please try logging in again.");
  }
  syncCrmActingAdvisorSession(sanitizedPayload?.user);
  localStorage.setItem("user", JSON.stringify(sanitizedPayload));
  return sanitizedPayload;
};

export const clearOversizedSession = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("oldUser");
  sessionStorage.removeItem("aiTermsServiceRetryAttempt");
  sessionStorage.removeItem("serviceInterruptionRetryAttempt");
};

export const loadUserFromStorage = () => {
  try {
    const storedUser = localStorage.getItem("user");
    if (!storedUser) return null;

    const parsed = JSON.parse(storedUser);
    const token = parsed?.token;
    if (typeof token === "string" && token.length > MAX_SESSION_TOKEN_CHARS) {
      console.warn("[auth] Clearing oversized session token from localStorage");
      clearOversizedSession();
      return null;
    }
    const sanitized = sanitizeAuthPayload(parsed);
    if (JSON.stringify(parsed) !== JSON.stringify(sanitized)) {
      localStorage.setItem("user", JSON.stringify(sanitized));
    }
    return sanitized;
  } catch {
    return null;
  }
};
